import axios from "axios";
import { Api_url } from "../../common/api_url";
import { axiosNoAuth } from "../../config/axiosInstances";
import {
  SET_DATE_RANGE,
  SET_RECRUITER_ACCESS_TOKEN,
  SET_RECRUITER_AUTH_DATA,
  SET_RECRUITER_DATA,
  SET_RECRUITER_FILTERS,
  SET_RECRUITER_UUID,
  SET_WISHLISTED_PROFILES,
  RESET_ALL_DATA,
} from "./types";
import { Mixpanel } from "../../config/Mixpanel";

const handleRecruiter401 = (dispatch, err, errorCallback) => {
  if (err?.response?.status === 401) {
    dispatch({ type: RESET_ALL_DATA });
  }

  if (errorCallback) {
    errorCallback(err);
  }
};

export function setDateRange(dateRange) {
  return {
    type: SET_DATE_RANGE,
    payload: dateRange,
  };
}

export function authorizeGoogle(successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axiosNoAuth.get(
        "https://resume-new-backend.learntube.ai/api/auth/google/authorize",
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      successCallback && successCallback(response.data?.authorization_url);
    } catch (err) {
      console.log(err);
      errorCallback && errorCallback(err);
    }
  };
}

export function signupWithGoogle(
  tokenID,
  state,
  successCallback,
  errorCallback,
) {
  // Handle backward compatibility where state might be the successCallback
  if (typeof state === "function") {
    errorCallback = successCallback;
    successCallback = state;
    state = "login";
  }

  return async function (dispatch) {
    try {
      const response = await axiosNoAuth.get(
        `https://resume-new-backend.learntube.ai/api/auth/google/callback?code=${tokenID}&state=${state}`,
      );

      if (response?.status === 200) {
        dispatch(getRecruiterDetails(response?.data?.access_token));
        dispatch({ type: SET_RECRUITER_ACCESS_TOKEN, payload: response?.data?.access_token });
        dispatch({ type: SET_RECRUITER_UUID, payload: response?.data?.user_id });
        dispatch({ type: SET_RECRUITER_AUTH_DATA, payload: response?.data });
        successCallback && successCallback(response.data);
      } else {
        errorCallback && errorCallback(response?.data);
      }
    } catch (err) {
      console.log(err);
      errorCallback && errorCallback(err);
    }
  };
}

export function getRecruiterDetails(token, successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axiosNoAuth.get(
        `https://resume-new-backend.learntube.ai/api/auth/me`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.status === 200) {
        dispatch({ type: SET_RECRUITER_DATA, payload: response.data });
        Mixpanel.identify(response.data?.email);
        Mixpanel.people.set({
          $name: response.data?.name,
          $email: response.data?.email,
          $phone: response.data?.phone,
          $requirement: response?.data?.$requirement,
          $organisation: response?.data?.organisation
        });
        successCallback && successCallback(response.data);
      } else {
        errorCallback && errorCallback(response?.data);
      }
    } catch (err) {
      console.log(err);
      errorCallback && errorCallback(err);
    }
  };
}

export function addRecruiterWishlist(token, candidate_email, successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai/api/recruiter/wishlist",
        {
          candidate_email: candidate_email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function getRecruiterWishlist(token, successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        "https://resume-new-backend.learntube.ai/api/recruiter/wishlist",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      dispatch({ type: SET_WISHLISTED_PROFILES, payload: response?.data });
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  }
}

export function removeRecruiterWishlist(token, candidate_email, successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axios.delete(
        `https://resume-new-backend.learntube.ai/api/recruiter/wishlist/${encodeURIComponent(candidate_email)}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function queryFilteredProfiles(
  access_token,
  companies,
  institutions,
  states,
  cities,
  skills,
  experience,
  page,
  page_size,
  successCallback,
  errorCallback,
) { 
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai/api/query_profiles",
        {
          companies: companies,
          institutions: institutions,
          states: states,
          cities: cities,
          skills: skills,
          experience: experience,
          page: page,
          page_size: page_size,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access_token}`,
          },
        },
      );
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function completeRecruiterProfile(
  token,
  country_code,
  whatsapp,
  organisation,
  requirements,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai/api/auth/google/complete-profile",
        {
          country_code: country_code,
          phone: whatsapp,
          organization_name: organisation,
          requirement: requirements,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      dispatch(getRecruiterDetails(token));
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function verifyRecruiterOTP(
  token,
  phone,
  otp,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai/api/auth/google/verify-otp",
        {
          phone: phone,
          otp: otp,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function fetchRelevantProfiles(
  nl_query,
  recruiterAccessToken,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai" + Api_url.GetRelevantProfiles,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${recruiterAccessToken}`,
          },
          params: {
            nl_query: nl_query,
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

// curl -X 'GET' \
  // 'https://resume-new-backend.learntube.ai/api/profiles_by_skill_category?skill_category=Specialized%20Engineering%20%26%20Safety&page=1&page_size=20' \
  // -H 'accept: application/json'

export function fetchProfilesBySkillCategory(
  recruiterUUID,
  skill_category,
  page,
  page_size,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const encodedSkill = encodeURIComponent(skill_category);
      const url = `https://resume-new-backend.learntube.ai/api/profiles_by_skill_category?skill_category=${encodedSkill}&page=${page}&page_size=${page_size}&uuid=${recruiterUUID}`;

      const response = await axios.get(
        url,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      console.log("response", response.data);
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function fetchRecruiterFilters(successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        "https://resume-new-backend.learntube.ai/api/filters",
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      dispatch({ type: SET_RECRUITER_FILTERS, payload: response.data });
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

// filter_type: One of 'companies', 'institutions', 'states', 'cities', 'skills'
export function fetchRecruiterSkills(
  filter_type,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        `https://resume-new-backend.learntube.ai/api/filters/${filter_type}`,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

// filter_type: One of 'companies', 'institutions', 'states', 'cities', 'skills'
// curl --request GET \
  // --url 'http://18.60.250.118:8501/api/filters/search/skills?query=java&limit=20' \
  // --header 'accept: application/json'

export function fetchRecruiterSearch(
  filter_type,
  query,
  limit,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        `https://resume-new-backend.learntube.ai/api/filters/search/${filter_type}?query=${query}&limit=${limit}`,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function checkInterviewRequest(
  candidate_email,
  token,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        `https://resume-new-backend.learntube.ai/api/recruiter/interview-request/check/${candidate_email}`,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function requestInterview(
  candidate_email,
  token,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai/api/recruiter/interview-request",
        {
          candidate_email: candidate_email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function fetchLeaderboard(
  subject,
  page,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://certified-backend.learntube.ai/leaderboard",
        {
          subject: subject,
          page: page,
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function fetchLeaderboardHistoric(
  subject,
  page,
  start_date,
  end_date,
  user_email,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://certified-backend.learntube.ai/leaderboard-historic",
        {
          subject: subject,
          page: page,
          start_date: start_date,
          end_date: end_date,
          user_email: user_email,
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function getProfileByEmail(email, successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        "https://resume-new-backend.learntube.ai" + Api_url.GetProfileByEmail,
        {
          params: {
            email: email,
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      if (errorCallback) {
        errorCallback(err);
      }
    }
  };
}

export function generateSignedUrl(gs_uri, token, successCallback, errorCallback) {
  return async function (dispatch) {
    try {
      const url = new URL("https://resume-new-backend.learntube.ai/generate_signed_url");
      url.searchParams.set("gs_uri", gs_uri);
      const response = await axios.post(url.toString(), null, {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      successCallback && successCallback(response.data?.url);
    } catch (err) {
      console.log(err.message);
      handleRecruiter401(dispatch, err, errorCallback);
    }
  };
}

export function fetchTopThousandUsers(
  page,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.get(
        `https://resume-new-backend.learntube.ai/api/ranked_users?page=${page}`,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function submitExitPopupLead(
  org_name,
  name,
  email,
  phone,
  requirement,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "https://resume-new-backend.learntube.ai/api/exit-popup/lead",
        {
          org_name: org_name,
          name: name,
          email: email,
          phone: phone,
          requirement: requirement,
        },
      );  
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function fetchRolesBySkill(
  category,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const encodedSkill = encodeURIComponent(category);
      const response = await axios.get(
        `http://18.60.250.118:8501/get_roles?category=${encodedSkill}`,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      successCallback && successCallback(response?.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

export function filterByRole(
  category,
  role,
  page,
  page_size,
  recruiter_uuid,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const encodedCategory = encodeURIComponent(category);
      const encodedRole = encodeURIComponent(role); 
      const response = await axios.get(
        `http://18.60.250.118:8501/api/filter_by_role?category=${encodedCategory}&role=${encodedRole}&page=${page}&page_size=${page_size}&recruiter_uuid=${recruiter_uuid}`,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}

// curl -X 'POST' \
  // 'http://18.60.250.118:8501/basic_search_profiles?nl_query=Digital%20marketing' \
  // -H 'accept: application/json' \
  // -H 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiYTE4NmViMDUtMWZlZS00NjNhLTkyN2YtZDM1ODEyNjgzNTc3IiwiZW1haWwiOiJjYXJlZXJuaW5qYXNlZGh1MUBnbWFpbC5jb20iLCJleHAiOjE3ODAwNzM5NDEsImlhdCI6MTc3MjI5Nzk0MX0.OPH1e2m5bUs9J2bbeb5GYKbhCfcXE-sYe8vjIDY-Co4' \
  // -d ''

export function basicSearchProfiles(
  nl_query,
  token,
  successCallback,
  errorCallback,
) {
  return async function (dispatch) {
    try {
      const response = await axios.post(
        "http://18.60.250.118:8501/basic_search_profiles?nl_query=" + nl_query,
        null,
        {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      successCallback && successCallback(response.data);
    } catch (err) {
      console.log(err.message);
      errorCallback && errorCallback(err);
    }
  };
}